/**
 * @fileoverview File Upload Security Hook
 * 
 * Provides security validation and context-based access control
 * for file uploads based on user permissions and security level.
 * 
 * Requirements: 1.6, 1.7, 9.1, 9.2
 */

import { useCallback, useMemo } from 'react';
import { useModuleContext } from '../../context/ModuleContextProvider.jsx';
import { getFileTypeInfo, formatFileSize } from '../types.js';

/**
 * File upload security hook
 * 
 * @param {FileUploadConfig} config - Upload configuration
 * @param {ModuleContext} context - Module context
 * @returns {Object} Security utilities
 */
export function useFileUploadSecurity(config, context) {
  const moduleContext = useModuleContext();

  // Security configuration based on context and level
  const securityConfig = useMemo(() => {
    const baseConfig = {
      maxFileSize: config.maxFileSize,
      allowedTypes: config.allowedTypes,
      scanContent: false,
      checkQuota: false,
      rateLimit: false
    };

    // Context-specific security settings
    switch (config.context) {
      case 'admin-kb':
        return {
          ...baseConfig,
          scanContent: true,
          checkQuota: false, // Admins have no quota limits
          rateLimit: false,
          maxFileSize: Math.max(baseConfig.maxFileSize, 10 * 1024 * 1024), // At least 10MB for admin
          allowedTypes: [...baseConfig.allowedTypes, 'json', 'csv'] // Additional types for admin
        };

      case 'parrot-config':
        return {
          ...baseConfig,
          scanContent: true,
          checkQuota: true,
          rateLimit: true,
          maxFileSize: Math.min(baseConfig.maxFileSize, 2 * 1024 * 1024) // Max 2MB for config files
        };

      case 'user-kb':
      default:
        return {
          ...baseConfig,
          scanContent: true,
          checkQuota: true,
          rateLimit: true
        };
    }
  }, [config]);

  // Required permissions based on context and security level
  const requiredPermissions = useMemo(() => {
    const permissions = [];

    switch (config.context) {
      case 'admin-kb':
        permissions.push('ADMIN_KB_UPLOAD', 'ADMIN_FILE_MANAGEMENT');
        break;
      case 'parrot-config':
        permissions.push('PARROT_CONFIG_UPLOAD', 'PARROT_MANAGEMENT');
        break;
      case 'user-kb':
      default:
        permissions.push('USER_KB_UPLOAD', 'FILE_UPLOAD');
        break;
    }

    if (config.securityLevel === 'admin') {
      permissions.push('ADMIN_ACCESS');
    }

    return permissions;
  }, [config.context, config.securityLevel]);

  /**
   * Validate security context and permissions
   */
  const validateContext = useCallback(() => {
    // Check if user has required permissions
    const missingPermissions = requiredPermissions.filter(
      permission => !moduleContext.hasPermission(permission)
    );

    if (missingPermissions.length > 0) {
      throw new Error(
        `Missing required permissions: ${missingPermissions.join(', ')}`
      );
    }

    // Check security level compatibility
    if (config.securityLevel === 'admin' && moduleContext.user.role !== 'admin') {
      throw new Error('Admin security level requires admin role');
    }

    // Validate context compatibility with user role
    if (config.context === 'admin-kb' && moduleContext.user.role !== 'admin') {
      throw new Error('Admin knowledge base context requires admin role');
    }

    return true;
  }, [requiredPermissions, config, moduleContext]);

  /**
   * Validate files for security compliance
   */
  const validateFiles = useCallback(async (files) => {
    const errors = [];
    const warnings = [];

    try {
      for (const file of files) {
        // File type validation
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!extension || !securityConfig.allowedTypes.includes(extension)) {
          errors.push(`File type not allowed: ${file.name}`);
          continue;
        }

        // File size validation
        if (file.size > securityConfig.maxFileSize) {
          errors.push(
            `File too large: ${file.name} (${formatFileSize(file.size)} > ${formatFileSize(securityConfig.maxFileSize)})`
          );
          continue;
        }

        // Empty file check
        if (file.size === 0) {
          errors.push(`Empty file: ${file.name}`);
          continue;
        }

        // File name validation
        if (file.name.length > 255) {
          errors.push(`File name too long: ${file.name}`);
          continue;
        }

        // Check for suspicious file names
        if (containsSuspiciousPatterns(file.name)) {
          errors.push(`Suspicious file name: ${file.name}`);
          continue;
        }

        // MIME type validation
        const fileTypeInfo = getFileTypeInfo(extension);
        if (fileTypeInfo && !fileTypeInfo.mimeTypes.includes(file.type)) {
          warnings.push(
            `MIME type mismatch for ${file.name}: expected ${fileTypeInfo.mimeTypes.join(' or ')}, got ${file.type}`
          );
        }

        // Content scanning (basic checks)
        if (securityConfig.scanContent) {
          const contentValidation = await validateFileContent(file);
          if (!contentValidation.valid) {
            errors.push(`Content validation failed for ${file.name}: ${contentValidation.error}`);
            continue;
          }
        }
      }

      // Check upload quota if enabled
      if (securityConfig.checkQuota) {
        const quotaValidation = await checkUploadQuota(files);
        if (!quotaValidation.valid) {
          errors.push(`Upload quota exceeded: ${quotaValidation.error}`);
        }
      }

      // Check rate limits if enabled
      if (securityConfig.rateLimit) {
        const rateLimitValidation = await checkRateLimit(files.length);
        if (!rateLimitValidation.valid) {
          errors.push(`Rate limit exceeded: ${rateLimitValidation.error}`);
        }
      }

      return {
        valid: errors.length === 0,
        errors,
        warnings
      };

    } catch (error) {
      console.error('Security validation error:', error);
      return {
        valid: false,
        errors: [`Security validation failed: ${error.message}`],
        warnings
      };
    }
  }, [securityConfig]);

  /**
   * Check for suspicious file name patterns
   */
  const containsSuspiciousPatterns = useCallback((fileName) => {
    const suspiciousPatterns = [
      /\.(exe|bat|cmd|scr|pif|com)$/i, // Executable files
      /\.(js|vbs|ps1|sh)$/i, // Script files
      /[<>:"|?*]/g, // Invalid characters
      /^\./,  // Hidden files
      /\.\./  // Directory traversal
    ];

    return suspiciousPatterns.some(pattern => pattern.test(fileName));
  }, []);

  /**
   * Validate file content (enhanced security checks)
   */
  const validateFileContent = useCallback(async (file) => {
    try {
      // Read first few bytes to check file signature
      const buffer = await readFileBytes(file, 0, 1024); // Read more bytes for better detection
      const bytes = new Uint8Array(buffer);

      // Check for common file signatures
      const extension = file.name.split('.').pop()?.toLowerCase();
      const expectedSignatures = getFileSignatures(extension);

      if (expectedSignatures.length > 0) {
        const hasValidSignature = expectedSignatures.some(signature =>
          signature.every((byte, index) => bytes[index] === byte)
        );

        if (!hasValidSignature) {
          return {
            valid: false,
            error: 'File signature does not match extension - possible file type spoofing'
          };
        }
      }

      // Enhanced malware signature detection
      const malwareCheck = await detectMalwareSignatures(bytes);
      if (!malwareCheck.valid) {
        return malwareCheck;
      }

      // Check for embedded scripts or suspicious content
      const textContent = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
      if (containsSuspiciousContent(textContent)) {
        return {
          valid: false,
          error: 'File contains suspicious content or potential malware'
        };
      }

      // Advanced content analysis for specific file types
      const advancedCheck = await performAdvancedContentAnalysis(file, bytes, extension);
      if (!advancedCheck.valid) {
        return advancedCheck;
      }

      // Check file entropy (randomness) - high entropy might indicate encryption/compression/malware
      const entropy = calculateFileEntropy(bytes);
      if (entropy > 7.5 && !['pdf', 'docx'].includes(extension)) {
        return {
          valid: false,
          error: 'File has unusually high entropy - possible encrypted or compressed malware'
        };
      }

      return { valid: true };

    } catch (error) {
      console.error('Content validation error:', error);
      return {
        valid: false,
        error: 'Failed to validate file content'
      };
    }
  }, []);

  /**
   * Check upload quota for user
   */
  const checkUploadQuota = useCallback(async (files) => {
    try {
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);
      
      // Get user's current usage and limits
      const client = (context && context.apiClient) || moduleContext.apiClient;
      if (!client || typeof client.get !== 'function') {
        // If no API client is available, do not block the upload
        return { valid: true };
      }
      const quotaInfo = await client.get('/api/user/quota');
      
      if (quotaInfo.used + totalSize > quotaInfo.limit) {
        return {
          valid: false,
          error: `Upload would exceed quota limit (${formatFileSize(quotaInfo.used + totalSize)} > ${formatFileSize(quotaInfo.limit)})`
        };
      }

      // Check file count limits
      if (quotaInfo.fileCount + files.length > quotaInfo.maxFiles) {
        return {
          valid: false,
          error: `Upload would exceed file count limit (${quotaInfo.fileCount + files.length} > ${quotaInfo.maxFiles})`
        };
      }

      return { valid: true };

    } catch (error) {
      console.error('Quota check error:', error);
      // Don't block upload if quota check fails, but log the error
      if (context.auditLogger) {
        context.auditLogger.logSecurityEvent({
          type: 'QUOTA_CHECK_FAILED',
          userId: moduleContext.user.id,
          action: 'check_upload_quota',
          success: false,
          metadata: { error: error.message, fileCount: files.length }
        });
      }
      return { valid: true };
    }
  }, [context, moduleContext]);

  /**
   * Check rate limits for uploads
   */
  const checkRateLimit = useCallback(async (fileCount) => {
    try {
      const client = (context && context.apiClient) || moduleContext.apiClient;
      if (!client || typeof client.get !== 'function') {
        return { valid: true };
      }
      const rateLimitInfo = await client.get('/api/user/rate-limit');
      
      if (rateLimitInfo.remaining < fileCount) {
        return {
          valid: false,
          error: `Rate limit exceeded. ${rateLimitInfo.remaining} uploads remaining, requested ${fileCount}. Reset at ${new Date(rateLimitInfo.resetTime).toLocaleTimeString()}`
        };
      }

      // Check for suspicious upload patterns
      if (fileCount > 10 && rateLimitInfo.recentUploads > 50) {
        return {
          valid: false,
          error: 'Suspicious upload pattern detected. Please contact support if this is legitimate.'
        };
      }

      return { valid: true };

    } catch (error) {
      console.error('Rate limit check error:', error);
      // Don't block upload if rate limit check fails, but log the error
      if (context.auditLogger) {
        context.auditLogger.logSecurityEvent({
          type: 'RATE_LIMIT_CHECK_FAILED',
          userId: moduleContext.user.id,
          action: 'check_rate_limit',
          success: false,
          metadata: { error: error.message, requestedFiles: fileCount }
        });
      }
      return { valid: true };
    }
  }, [context, moduleContext]);

  return {
    securityConfig,
    requiredPermissions,
    validateContext,
    validateFiles
  };
}

/**
 * Read specific bytes from a file
 */
async function readFileBytes(file, start, length) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file.slice(start, start + length));
  });
}

/**
 * Get expected file signatures for validation
 */
function getFileSignatures(extension) {
  const signatures = {
    'pdf': [[0x25, 0x50, 0x44, 0x46]], // %PDF
    'docx': [[0x50, 0x4B, 0x03, 0x04]], // ZIP signature (DOCX is ZIP-based)
    'txt': [], // Text files don't have a specific signature
    'html': [], // HTML files don't have a specific signature
    'json': [], // JSON files don't have a specific signature
    'csv': []   // CSV files don't have a specific signature
  };

  return signatures[extension] || [];
}

/**
 * Check for suspicious content in file
 */
function containsSuspiciousContent(content) {
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /vbscript:/i,
    /onload=/i,
    /onerror=/i,
    /eval\(/i,
    /document\.write/i,
    /window\.location/i,
    // Additional malicious patterns
    /cmd\.exe/i,
    /powershell/i,
    /\/bin\/sh/i,
    /\/bin\/bash/i,
    /system\(/i,
    /exec\(/i,
    /shell_exec/i,
    /passthru/i,
    // Suspicious base64 patterns
    /data:.*base64/i,
    // SQL injection patterns
    /union.*select/i,
    /drop.*table/i,
    /insert.*into/i,
    // XSS patterns
    /alert\(/i,
    /confirm\(/i,
    /prompt\(/i
  ];

  return suspiciousPatterns.some(pattern => pattern.test(content));
}

/**
 * Detect malware signatures in file bytes
 */
async function detectMalwareSignatures(bytes) {
  // Common malware signatures (simplified for demo - in production use proper antivirus API)
  const malwareSignatures = [
    // PE executable signatures
    [0x4D, 0x5A], // MZ header (DOS/Windows executable)
    // ELF executable signatures  
    [0x7F, 0x45, 0x4C, 0x46], // ELF header (Linux executable)
    // Mach-O executable signatures
    [0xFE, 0xED, 0xFA, 0xCE], // Mach-O 32-bit
    [0xFE, 0xED, 0xFA, 0xCF], // Mach-O 64-bit
    // ZIP bomb signatures (nested ZIP files)
    [0x50, 0x4B, 0x03, 0x04, 0x50, 0x4B, 0x03, 0x04], // Multiple ZIP headers
    // Suspicious macro signatures
    [0xD0, 0xCF, 0x11, 0xE0, 0xA1, 0xB1, 0x1A, 0xE1] // OLE compound document (could contain macros)
  ];

  for (const signature of malwareSignatures) {
    if (signature.every((byte, index) => bytes[index] === byte)) {
      return {
        valid: false,
        error: 'File contains potential malware signature'
      };
    }
  }

  // Check for suspicious byte patterns
  const suspiciousPatterns = [
    // Shellcode patterns
    /\x90{10,}/, // NOP sled
    /\xCC{5,}/, // INT3 instructions
    // Obfuscated content
    /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\xFF]{50,}/ // High concentration of non-printable characters
  ];

  const bytesAsString = Array.from(bytes).map(b => String.fromCharCode(b)).join('');
  for (const pattern of suspiciousPatterns) {
    if (pattern.test(bytesAsString)) {
      return {
        valid: false,
        error: 'File contains suspicious byte patterns'
      };
    }
  }

  return { valid: true };
}

/**
 * Perform advanced content analysis for specific file types
 */
async function performAdvancedContentAnalysis(file, bytes, extension) {
  try {
    switch (extension) {
      case 'pdf':
        return await analyzePDFContent(bytes);
      case 'docx':
        return await analyzeDocxContent(bytes);
      case 'html':
        return await analyzeHTMLContent(file);
      case 'json':
        return await analyzeJSONContent(file);
      default:
        return { valid: true };
    }
  } catch (error) {
    console.error('Advanced content analysis error:', error);
    return {
      valid: false,
      error: 'Failed to perform advanced content analysis'
    };
  }
}

/**
 * Analyze PDF content for malicious elements
 */
async function analyzePDFContent(bytes) {
  const pdfContent = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  
  // Check for suspicious PDF elements
  const suspiciousPDFPatterns = [
    /\/JavaScript/i,
    /\/JS/i,
    /\/OpenAction/i,
    /\/Launch/i,
    /\/EmbeddedFile/i,
    /\/XFA/i, // XML Forms Architecture (can contain malicious code)
    /\/RichMedia/i,
    /\/3D/i // 3D content can contain malicious code
  ];

  for (const pattern of suspiciousPDFPatterns) {
    if (pattern.test(pdfContent)) {
      return {
        valid: false,
        error: 'PDF contains potentially malicious elements (JavaScript, embedded files, etc.)'
      };
    }
  }

  return { valid: true };
}

/**
 * Analyze DOCX content for malicious elements
 */
async function analyzeDocxContent(bytes) {
  // DOCX files are ZIP archives, check for suspicious content
  const docxContent = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  
  // Check for macro-related content
  const suspiciousDocxPatterns = [
    /vbaProject\.bin/i,
    /macros/i,
    /Microsoft\.VSTO/i,
    /AutoExec/i,
    /AutoOpen/i,
    /Document_Open/i,
    /Workbook_Open/i
  ];

  for (const pattern of suspiciousDocxPatterns) {
    if (pattern.test(docxContent)) {
      return {
        valid: false,
        error: 'DOCX contains potentially malicious macros or VBA code'
      };
    }
  }

  return { valid: true };
}

/**
 * Analyze HTML content for malicious elements
 */
async function analyzeHTMLContent(file) {
  const htmlContent = await file.text();
  
  // Enhanced HTML security checks
  const maliciousHTMLPatterns = [
    /<iframe[^>]*src\s*=\s*["']?(?:javascript:|data:|vbscript:)/i,
    /<object[^>]*data\s*=\s*["']?(?:javascript:|data:|vbscript:)/i,
    /<embed[^>]*src\s*=\s*["']?(?:javascript:|data:|vbscript:)/i,
    /<link[^>]*href\s*=\s*["']?(?:javascript:|data:|vbscript:)/i,
    /<meta[^>]*content\s*=\s*["'][^"']*(?:javascript:|data:|vbscript:)/i,
    /<form[^>]*action\s*=\s*["']?(?:javascript:|data:|vbscript:)/i,
    // Check for suspicious event handlers
    /on\w+\s*=\s*["'][^"']*(?:eval|setTimeout|setInterval|Function|XMLHttpRequest)/i,
    // Check for suspicious CSS
    /expression\s*\(/i,
    /behavior\s*:/i,
    /-moz-binding/i
  ];

  for (const pattern of maliciousHTMLPatterns) {
    if (pattern.test(htmlContent)) {
      return {
        valid: false,
        error: 'HTML contains potentially malicious code or suspicious elements'
      };
    }
  }

  return { valid: true };
}

/**
 * Analyze JSON content for malicious elements
 */
async function analyzeJSONContent(file) {
  try {
    const jsonContent = await file.text();
    const parsed = JSON.parse(jsonContent);
    
    // Check for suspicious JSON content
    const jsonString = JSON.stringify(parsed);
    if (containsSuspiciousContent(jsonString)) {
      return {
        valid: false,
        error: 'JSON contains potentially malicious content'
      };
    }

    // Check for excessively deep nesting (potential DoS)
    const maxDepth = calculateObjectDepth(parsed);
    if (maxDepth > 20) {
      return {
        valid: false,
        error: 'JSON has excessive nesting depth - potential DoS attack'
      };
    }

    return { valid: true };
  } catch (error) {
    return {
      valid: false,
      error: 'Invalid JSON format'
    };
  }
}

/**
 * Calculate file entropy (measure of randomness)
 */
function calculateFileEntropy(bytes) {
  const frequency = new Array(256).fill(0);
  
  // Count byte frequencies
  for (const byte of bytes) {
    frequency[byte]++;
  }
  
  // Calculate entropy
  let entropy = 0;
  const length = bytes.length;
  
  for (const count of frequency) {
    if (count > 0) {
      const probability = count / length;
      entropy -= probability * Math.log2(probability);
    }
  }
  
  return entropy;
}

/**
 * Calculate object depth for nested structures
 */
function calculateObjectDepth(obj, depth = 0) {
  if (depth > 50 || typeof obj !== 'object' || obj === null) {
    return depth;
  }

  let maxDepth = depth;
  for (const value of Object.values(obj)) {
    if (typeof value === 'object' && value !== null) {
      maxDepth = Math.max(maxDepth, calculateObjectDepth(value, depth + 1));
    }
  }

  return maxDepth;
}
